import { align, display } from '@/styles/display'
import { fontSize } from '@/styles/font'
import spaceStyle from '@/styles/space'
import { SubButton } from '../base/Button'
import { buttonSize } from '../base/Button/Base'
import { Row } from '../base/Layout'
import {
  type V1FormElement,
  type V1FormElementState,
  type V1FormElementUiTextAndSelectorPopup,
  type V1FormElementContentState,
  V1FormElementStateType,
  type V1SelectorElement
} from 'mnvm-mypage-app-ts-if'
import { Text3 } from '../base/Text'
import _, { isBoolean } from 'lodash'
import { useCallback, useEffect, useState } from 'react'
import SelectorPopContainer from '@/feature/Builder/Component/containers/SelectorPopupContainer'
import { Block } from '../base/Block'
import { useComboBoxSelectorElements } from '@/feature/Builder/Component/api/getComboBoxSelectorElements'
import { showErrorToast } from '../SnackBar/BaseSnackBar'
import { AutoSuggestInputComponent } from '../base/Form/InputText'
import type Autosuggest from 'react-autosuggest'
import InputErrorLabel from '../base/Form/InputErrorLabel'

export const FormElementComboBoxAndSelectorPopup = ({
  form,
  onChange,
  isEdit,
  formElementState,
  elemenErrorMessage
}: {
  isEdit: boolean
  form: V1FormElement
  formElementState: V1FormElementState
  onChange: (newState: V1FormElementState) => void
  elemenErrorMessage?: string
}): JSX.Element => {
  const { ui } = form
  const { popup, readOnly } = ui as V1FormElementUiTextAndSelectorPopup
  const [isOpen, setIsOpen] = useState(false)

  const [value, setValue] = useState('')
  const [suggestions, setSuggestions] = useState<V1SelectorElement[]>([])
  const isReadOnly = isBoolean(readOnly) && readOnly

  const { mutateAsync } = useComboBoxSelectorElements()

  const debouncedGetSuggestions = useCallback(
    _.debounce((newValue) => {
      getSuggestions(newValue)
    }, 1000),
    []
  )

  const getSuggestions = (value: string): void => {
    if (popup?.viewId !== undefined) {
      void mutateAsync({
        viewId: popup?.viewId ?? '',
        keyword: value
      })
        .then((res) => {
          setSuggestions(res.elements ?? [])
        })
        .catch((err) => {
          showErrorToast(String(err))
        })
    }
  }

  const onSuggestionsClearRequested = (): void => {
    setSuggestions([])
  }

  const onSuggestionSelected = (
    _: React.FormEvent,
    data: Autosuggest.SuggestionSelectedEventData<V1SelectorElement>
  ): void => {
    const value = data.suggestion
    if (value.label === undefined) return
    setValue(value.label)
    setSuggestions([])
    const contentStates: V1FormElementContentState[] = [
      { text: value.data, label: value.label }
    ]

    const state: V1FormElementState = {
      ...formElementState,
      contentStates
    }
    onChange(state)
  }

  const getSuggestionValue = (suggestion: V1SelectorElement): string =>
    suggestion.label ?? ''

  const renderSuggestion = (suggestion: V1SelectorElement): JSX.Element => (
    <Block
      padding={{
        vertical: spaceStyle.S
      }}
    >
      <Text3 textAlign={align.LEFT} margin={{ all: spaceStyle.NONE }}>
        {suggestion.label}
      </Text3>
    </Block>
  )

  const defaultSelected = (formElementState?.contentStates ?? []).map((e) => {
    switch (formElementState?.type) {
      case V1FormElementStateType.Uint32:
      case V1FormElementStateType.Uint32List:
        return e.uint32?.toString() ?? ''
      case V1FormElementStateType.Text:
      case V1FormElementStateType.TextList:
        return e.text ?? ''
      case V1FormElementStateType.Bool:
      case V1FormElementStateType.BoolList:
        return e.bool?.toString() ?? ''
      default:
        return ''
    }
  })

  const selectedLabel = (formElementState?.contentStates ?? [])
    .map((e) => {
      return e.label?.toString() ?? ''
    })
    .join('/')

  useEffect(() => {
    setValue(selectedLabel)
  }, [])

  if (!isEdit) {
    return (
      <Text3 textAlign={align.LEFT} margin={{ all: spaceStyle.NONE }}>
        {selectedLabel}
      </Text3>
    )
  }

  return (
    <>
      <Row
        gutter={value !== '' ? 8 : 0}
        align={align.CENTER}
        style={{ width: 'fỉt-content' }}
      >
        {isReadOnly ? (
          <Text3
            truncate
            textAlign={align.LEFT}
            margin={{ all: spaceStyle.NONE }}
          >
            {value}
          </Text3>
        ) : (
          <Block display={display.FLEX}>
            <AutoSuggestInputComponent<V1SelectorElement>
              suggestions={suggestions}
              onSuggestionsClearRequested={onSuggestionsClearRequested}
              onSuggestionsFetchRequested={({ value }) => {
                setValue(value)
                debouncedGetSuggestions(value)
              }}
              onSuggestionSelected={onSuggestionSelected}
              getSuggestionValue={getSuggestionValue}
              renderSuggestion={renderSuggestion}
              renderInputComponent={(inputProps) => {
                inputProps.type = 'search'
                inputProps.autoCorrect = 'off'
                inputProps.spellCheck = false
                return <input {...inputProps} />
              }}
              inputProps={{
                placeholder: '',
                value,
                onChange: (_, { newValue, method }) => {
                  setValue(newValue)
                }
              }}
              highlightFirstSuggestion={true}
              isError={elemenErrorMessage !== undefined}
            />
          </Block>
        )}

        <SubButton
          maxWidth="72px"
          height={buttonSize.S}
          onClick={() => {
            setIsOpen(true)
          }}
          fontSize={fontSize.S}
          padding={{
            vertical: spaceStyle.M,
            horizontal: spaceStyle.XL
          }}
        >
          {isReadOnly ? '選択' : '検索'}
        </SubButton>
      </Row>
      {popup?.viewId !== undefined ? (
        <SelectorPopContainer
          isOpen={isOpen}
          viewId={popup?.viewId ?? ''}
          onSelect={(items, text) => {
            setIsOpen(false)
            const createNewState = (
              item: string
            ): V1FormElementContentState => {
              switch (formElementState?.type) {
                case V1FormElementStateType.Uint32:
                case V1FormElementStateType.Uint32List:
                  return { uint32: _.parseInt(item), label: text }
                case V1FormElementStateType.Text:
                case V1FormElementStateType.TextList:
                  return { text: item, label: text }
                case V1FormElementStateType.Bool:
                case V1FormElementStateType.BoolList:
                  return {
                    bool:
                      item.toLowerCase() === 'true' ||
                      item.toLowerCase() === '1',
                    label: text
                  }
                default:
                  return {}
              }
            }
            const contentStates: V1FormElementContentState[] =
              items.map(createNewState)
            const state: V1FormElementState = {
              ...formElementState,
              contentStates: contentStates.length === 0 ? [] : contentStates
            }
            onChange(state)
            setValue(text)
          }}
          defaultSelected={defaultSelected}
          onClose={() => {
            setIsOpen(false)
          }}
        />
      ) : undefined}
      {elemenErrorMessage !== undefined && (
        <InputErrorLabel message={elemenErrorMessage} />
      )}
    </>
  )
}
